import { cn } from '@/utils/cn';
import { formatCheckNumber, formatCurrency, formatDateRange, formatShortDate, formatText } from '@/utils/format';
import type { ClaimRow } from '../types/claims.types';
import { statusBadgeClass } from '../utils/claim-status';

/** The columns shared by a claim row and by its line entries. */
export function ClaimCells({ claim }: { claim: ClaimRow }): React.JSX.Element {
  return (
    <>
      <td className="px-3 py-2">{formatText(claim.npi)}</td>
      <td className="px-3 py-2">{formatText(claim.icn)}</td>
      <td className="px-3 py-2">{formatText(claim.location)}</td>
      <td className="px-3 py-2">{formatText(claim.procCode)}</td>
      <td className="px-3 py-2">{formatText(claim.svcCode)}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        {formatDateRange(claim.serviceDateFrom, claim.serviceDateTo)}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">{formatShortDate(claim.paymentDate)}</td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
            statusBadgeClass(claim.status),
          )}
        >
          {formatText(claim.status)}
        </span>
      </td>
      {/* Populated on claim entries only (US-009 AC-004); blank rather than a placeholder on a claim
          header, which carries no reason at all. Truncated so a long message cannot triple the height
          of every row — the detail panel shows it in full. */}
      <td title={claim.reason ?? undefined} className="text-muted max-w-[240px] truncate px-3 py-2">
        {claim.reason}
      </td>
      <td className="px-3 py-2 text-right">{formatCurrency(claim.billedAmount)}</td>
      <td className="px-3 py-2 text-right">{formatCurrency(claim.medicareAllowedAmount)}</td>
      <td className="px-3 py-2 text-right">{formatCurrency(claim.paidAmount)}</td>
      <td className="px-3 py-2 text-right">{formatCurrency(claim.carrierPaidAmount)}</td>
      <td className="px-3 py-2">{formatCheckNumber(claim.checkNumber)}</td>
    </>
  );
}
